import React from 'react';
import { KeyRound, Plus, Copy, Trash2 } from 'lucide-react';

/**
 * InviteCodesCard — invite code create form + active codes table with
 * copy-link, delete, and tier-grant editing. Stateless; parent supplies
 * codes + form state + handlers. Extracted from Admin.jsx.
 */
const InviteCodesCard = ({
  codes,
  // create form state + setters
  newCode, setNewCode,
  newLabel, setNewLabel,
  newMaxUses, setNewMaxUses,
  newTierGrant, setNewTierGrant,
  creating,
  onCreate,
  // row actions
  onDelete,
  copied,
  onCopy,
  inviteLink,
}) => (
  <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 mb-10">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-enf-accent-fg" />
        <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Invite codes</div>
      </div>
      <span className="text-xs text-enf-text-muted">{codes.length} active</span>
    </div>

    {/* New code form */}
    <form onSubmit={onCreate} className="grid sm:grid-cols-4 gap-2 mb-5 p-4 bg-enf-surface-mid border border-enf-surface-border rounded-lg" data-testid="invite-create-form">
      <input
        type="text" placeholder="CODE" value={newCode}
        onChange={(e) => setNewCode(e.target.value.toUpperCase())}
        className="px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm font-mono focus:outline-none focus:border-enf-accent-fg"
        required minLength={4}
        data-testid="invite-new-code"
      />
      <input
        type="text" placeholder="Label (e.g. board-2026)" value={newLabel}
        onChange={(e) => setNewLabel(e.target.value)}
        className="px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
        data-testid="invite-new-label"
      />
      <input
        type="number" placeholder="Max uses" value={newMaxUses} min={1} max={10000}
        onChange={(e) => setNewMaxUses(e.target.value)}
        className="px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
        data-testid="invite-new-maxuses"
      />
      <select
        value={newTierGrant}
        onChange={(e) => setNewTierGrant(e.target.value)}
        className="px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg cursor-pointer"
        data-testid="invite-new-tier-grant"
        title="Auto-grant a lifetime tier when this code is redeemed at signup"
      >
        <option value="egnite">Tier: Egnite (no grant)</option>
        <option value="comp">Tier: Comp (free grant)</option>
        <option value="founder">Tier: Founder ($249)</option>
        <option value="hero">Tier: Hero ($999)</option>
      </select>
      <button type="submit" disabled={creating}
        className="px-3 py-2 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
        data-testid="invite-create-submit"
      >
        <Plus className="w-4 h-4" /> {creating ? 'Creating...' : 'Add'}
      </button>
    </form>

    {/* Codes table */}
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-widest text-enf-text-muted border-b border-enf-surface-border">
          <tr>
            <th className="text-left font-semibold py-2 pr-3">Code</th>
            <th className="text-left font-semibold py-2 pr-3">Label</th>
            <th className="text-left font-semibold py-2 pr-3">Tier grant</th>
            <th className="text-left font-semibold py-2 pr-3">Used / Max</th>
            <th className="text-left font-semibold py-2 pr-3">Invite link</th>
            <th className="text-right font-semibold py-2"></th>
          </tr>
        </thead>
        <tbody>
          {codes.length === 0 ? (
            <tr><td colSpan={5} className="text-center text-enf-text-muted py-6">No codes yet.</td></tr>
          ) : codes.map((c) => {
            const exhausted = c.used_count >= c.max_uses;
            return (
              <tr key={c.code} className="border-b border-enf-surface-border/60" data-testid={`invite-row-${c.code}`}>
                <td className="py-3 pr-3 font-mono text-enf-accent-fg font-bold">{c.code}</td>
                <td className="py-3 pr-3 text-enf-text-secondary capitalize">{c.label}</td>
                <td className="py-3 pr-3 text-enf-text-secondary">
                  <span className={exhausted ? 'text-enf-role-warning' : 'text-enf-text-primary'}>{c.used_count}</span>
                  <span className="text-enf-text-muted"> / {c.max_uses}</span>
                </td>
                <td className="py-3 pr-3">
                  <button onClick={() => onCopy(inviteLink(c.code))} className="text-xs text-enf-role-info hover:text-enf-text-primary flex items-center gap-1.5 transition-colors" data-testid={`invite-copy-link-${c.code}`}>
                    <Copy className="w-3 h-3" /> {copied === inviteLink(c.code) ? 'Copied!' : 'Copy invite link'}
                  </button>
                </td>
                <td className="py-3 text-right">
                  <button onClick={() => onDelete(c.code)} className="text-enf-text-muted hover:text-enf-role-warning p-1 transition-colors" data-testid={`invite-delete-${c.code}`} aria-label={`Delete ${c.code}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export default InviteCodesCard;
